<template>
  <div v-if="showAdminWarning" class="p-16 bg-bg-warning text-text-warning type-sm">
    This component is not available on corporate domains
  </div>
  <div
    v-else-if="!isNonEcomSite && !loaded"
    class="text-center"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <LoadingIcon />
  </div>
  <div
    v-else-if="!isNonEcomSite"
    v-editable="blok"
    class="FindRetailerBlock"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div class="relative w-full h-auto desk:h-[calc(100dvh-160px)] max-h-auto desk:max-h-[990px] min-h-auto desk:min-h-[560px] bg-bg-secondary">
      <!-- Search & Filter Wrapper -->
      <div
        class="
        ListWrapper w-full max-w-auto desk:max-w-360 max-h-[calc(100%-48px)] overflow-y-auto
        p-16 mobOnly:pt-32 desk:p-24 relative desk:absolute top-0 desk:top-24 left-0 desk:left-24 z-10
        flex flex-col items-stretch gap-24
        bg-bg-subtle outline outline-0 desk:outline-1 outline-bg-inverse/10 shadow-none desk:shadow-lg"
      >
        <div class="flex flex-col items-stretch gap-12 desk:gap-16">
          <!-- Headline -->
          <div class="type-headline-lg">
            {{ blok.name }}
          </div>

          <!-- Search and Filter -->
          <div class="w-full flex gap-8">
            <TextInput
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              :is-search="true"
              :placeholder="$t('findRetailer.searchPlaceholder')"
              class="w-full"
              input-class="placeholder:text-12"
            />
            <button
              class="btn btn--lg min-w-48 gap-8"
              type="button"
              @click="openFilterModal"
            >
              <img
                src="/icons/filter-inv.svg"
                class="w-20 h-20"
                alt=""
              >
              <span v-if="activeFilterCount > 0"> {{ activeFilterCount }} </span>
            </button>
          </div>

          <!-- Use my location -->
          <button
            class="inline-flex items-center gap-8 type-headline-base underline underline-offset-4 decoration-1"
            type="button"
            @click="useCurrentLocation"
          >
            <img
              src="/icons/location.svg"
              class="w-20 h-20"
              alt=""
            >
            {{ $t('findRetailer.currentLocation') }}
          </button>
        </div>

        <!-- Retailer List Desktop -->
        <div v-if="hasInteracted && !isMobile">
          <div class="type-sm mb-16 empty:hidden">{{ showNear }}</div>
          <div v-if="filteredStores.length" class="w-full flex flex-col items-stretch gap-8">
            <FindRetailerStore
              v-for="store in visibleStores"
              :key="store.id"
              :ref="el => { if (el) storeRefs[store.id] = el }"
              :store="store"
              :expanded="expandedStoreId === store.id"
              :display-map="false"
              :info-window="false"
              @select="id => { openStore(id); zoomToStore(visibleStores.find(s => s.id === id)!) }"
              @close="closeDetails"
            />
          </div>
          <div
            v-else-if="!extendingSearch"
            class="w-full flex flex-col items-center justify-center p-24 text-center border border-dashed border-border-base"
          >
            <p class="type-headline-sm mb-4">{{ $t('findRetailer.noResults.title') }}</p>
            <p class="type-sm text-text-subtle">{{ $t('findRetailer.noResults.message') }}</p>
            <button
              class="btn btn--secondary mt-16"
              type="button"
              @click="clearFilter"
            >
              {{ $t('findRetailer.noResults.clearFilters') }}
            </button>
          </div>
          <LoadingIcon v-else />
        </div>
      </div>

      <!-- Tabs -->
      <div v-if="isMobile" class="flex bg-bg-subtle type-headline-base">
        <button
          class="flex grow items-center justify-center p-12"
          :class="{
            'border-border-inverse border-b-2': currentTab === 'map',
            'border-border-base border-b text-text-inactive': currentTab !== 'map'
          }"
          type="button"
          @click="currentTab = 'map'"
        >
          {{ $t('findRetailer.tabs.mapView') }}
        </button>
        <button
          class="flex grow items-center justify-center p-12 border-b-2"
          :class="{
            'border-border-inverse border-b-2': currentTab === 'list',
            'border-border-base border-b text-text-inactive': currentTab !== 'list'
          }"
          type="button"
          @click="currentTab = 'list'"
        >
          {{ $t('findRetailer.tabs.listView') }}
        </button>
      </div>

      <!-- Map Wrapper -->
      <div v-if="showMap" class="MapWrapper mobOnly:h-480 flex items-center justify-center w-full h-full relative">
        <GoogleMap
          ref="mapRef"
          :api-key="runtimeConfig.public.googleMapsApiKey"
          style="width: 100%; height: 100%"
          :styles="mapStyles"
          :center="center"
          :zoom="6"
          :disable-default-ui="true"
          @idle="onMapIdle"
        >
          <LoadingIcon v-if="!mapReady" class="absolute top-half left-half -translate-x-half -translate-y-half" />
          <template v-else>
            <MarkerCluster :options="clusterOptions">
              <Marker
                v-for="store in visibleMapStores"
                :key="store.id"
                :options="{
                  position: { lat: store.latitude, lng: store.longitude },
                  icon: (expandedStoreId === store.id || hoveredStoreId === store.id) ? selectedIcon : customIcon,
                }"
                @click="() => openStore(store.id)"
                @mouseover="() => hoveredStoreId = store.id"
                @mouseout="() => hoveredStoreId = null"
              />
            </MarkerCluster>

            <!-- Single InfoWindow that moves to the selected marker -->
            <InfoWindow
              v-if="expandedStore"
              v-model="infoWindowOpen"
              :options="infoWindowOptions"
            >
              <div id="content">
                <div id="siteNotice" />
                <div id="bodyContent">
                  <FindRetailerStore
                    :key="expandedStore.id"
                    :store="expandedStore"
                    :expanded="true"
                    :display-map="false"
                    :info-window="true"
                    @select="id => { openStore(id); }"
                    @close="closeDetails"
                  />
                </div>
              </div>
            </InfoWindow>
          </template>
        </GoogleMap>

        <!-- Zoom Controls -->
        <div class="absolute bottom-24 right-24 flex flex-col gap-8 z-20">
          <button
            class="w-40 h-40 flex items-center justify-center bg-bg-subtle shadow-lg border border-border-base hover:bg-bg-base transition-colors"
            :disabled="!mapReady"
            type="button"
            @click="zoomIn"
          >
            <span class="type-headline-lg">+</span>
          </button>
          <button
            class="w-40 h-40 flex items-center justify-center bg-bg-subtle shadow-lg border border-border-base hover:bg-bg-base transition-colors"
            :disabled="!mapReady"
            type="button"
            @click="zoomOut"
          >
            <span class="type-headline-lg">−</span>
          </button>
        </div>
      </div>

      <!-- Retailer List Mobile -->
      <div v-if="currentTab === 'list' && isMobile" class="p-16 bg-bg-subtle">
        <div v-if="filteredStores.length" class="w-full flex flex-col items-stretch gap-8">
          <FindRetailerStore
            v-for="store in paginatedStores"
            :key="store.id"
            :ref="el => { if (el) storeRefs[store.id] = el }"
            :store="store"
            :expanded="expandedStoreId === store.id"
            :display-map="true"
            :info-window="false"
            @select="id => { openStore(id); zoomToStore(paginatedStores.find(s => s.id === id)!) }"
            @close="closeDetails"
          />
        </div>

        <!-- LoadProgress Component -->
        <LoadProgress
          v-if="filteredStores.length"
          :current="paginatedStores.length"
          :total="filteredStores.length"
          text="findRetailer.load.quantityText"
          button-text="findRetailer.load.showMoreCta"
          class="py-32"
          @load-more="loadMore"
        />

        <!-- No Results Message -->
        <div
          v-if="filteredStores.length < 1"
          class="w-full flex flex-col items-center justify-center p-24 text-center border border-dashed border-border-base"
        >
          <p class="type-headline-sm mb-4">{{ $t('findRetailer.noResults.title') }}</p>
          <p class="type-sm text-text-subtle">{{ $t('findRetailer.noResults.message') }}</p>
          <button
            class="btn btn--secondary mt-16"
            type="button"
            @click="clearFilter"
          >
            {{ $t('findRetailer.noResults.clearFilters') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Modal -->
    <Teleport to="body">
      <transition name="fromLeft">
        <GenericSideMenu
          v-if="showFilterModal"
          position="left"
          :title="$t('findRetailer.filter.title')"
          :self-contained="true"
          @close="closeFilterModal"
        >
          <div class="divide-y divide-border-subtle">
            <!-- Sort order -->
            <div class="pb-16">
              <fieldset>
                <legend class="type-headline-sm mb-12">{{ $t('findRetailer.filter.sort') }}</legend>
                <ul class="flex flex-col items-start justify-start gap-12">
                  <li>
                    <ClientOnly>
                      <label
                        class="flex items-center justify-start gap-8"
                        :class="{ 'opacity-50': !hasDistanceReference }"
                      >
                        <input
                          v-model="sortOption"
                          type="radio"
                          name="sort"
                          value="distance"
                          :disabled="!hasDistanceReference"
                          @click="onSortOptionClick"
                        >
                        <span class="type-sm cursor-pointer">
                          {{ $t('findRetailer.filter.distance') }}
                        </span>
                      </label>
                      <template #fallback>
                        <label class="flex items-center justify-start gap-8 opacity-50">
                          <input
                            type="radio"
                            name="sort"
                            value="distance"
                            disabled
                          >
                          <span class="type-sm">
                            {{ $t('findRetailer.filter.distance') }}
                          </span>
                        </label>
                      </template>
                    </ClientOnly>
                  </li>
                  <li v-if="!globalContent.disableRetailerExhibitionDetails">
                    <label class="flex items-center justify-start gap-8">
                      <input
                        v-model="sortOption"
                        type="radio"
                        name="sort"
                        value="exhibitionSize"
                        @click="onSortOptionClick"
                      >
                      <span class="type-sm cursor-pointer">{{ $t('findRetailer.filter.exhibitionSize') }}</span>
                    </label>
                  </li>
                </ul>
              </fieldset>
            </div>

            <!-- Exibition type filter -->
            <div v-if="!globalContent.disableRetailerExhibitionDetails" class="py-16">
              <fieldset>
                <legend class="type-headline-sm mb-12">{{ $t('findRetailer.filter.exhibition') }}</legend>
                <ul class="flex flex-col gap-12">
                  <li
                    v-for="ex in exhibitions"
                    :key="ex.name"
                  >
                    <label class="flex items-center gap-8">
                      <input
                        v-model="selectedExhibitions"
                        type="checkbox"
                        :value="ex.name"
                        class="form-checkbox"
                      >
                      <span class="type-sm cursor-pointer">{{ ex.name }}</span>
                    </label>
                  </li>
                </ul>
              </fieldset>
            </div>

            <!-- Floor Types filter -->
            <div class="py-16">
              <fieldset>
                <legend class="type-headline-sm mb-12">{{ $t('findRetailer.filter.floorTypes') }}</legend>
                <ul class="flex flex-col gap-12">
                  <li v-for="floor in floorTypes" :key="floor">
                    <label class="flex items-center gap-8">
                      <input
                        v-model="selectedFloors"
                        type="checkbox"
                        class="form-checkbox"
                        :value="floor"
                      >
                      <span class="type-sm cursor-pointer">{{ floor }}</span>
                    </label>
                  </li>
                </ul>
              </fieldset>
            </div>

            <!-- Exhibition info list -->
            <div v-if="!globalContent.disableRetailerExhibitionDetails" class="py-16">
              <ul class="flex flex-col gap-16">
                <li
                  v-for="ex in exhibitions"
                  :key="ex.name"
                  class="flex flex-col"
                >
                  <div class="flex items-center gap-4 mb-6">
                    <div
                      v-for="n in MAX_RATING"
                      :key="n"
                      class="w-6 h-6 rounded-full"
                      :class="n <= ex.rating
                        ? 'bg-bg-brandStrong'
                        : 'bg-bg-base border border-border-base'"
                    />
                  </div>
                  <div class="type-headline-sm"> {{ ex.name }} </div>
                  <div class="type-sm text-text-subtle"> {{ ex.description }}</div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Apply/Clear Filter -->
          <template #bottom>
            <div class="w-full p-16 desk:p-24 flex gap-12 border-t border-border-subtle">
              <button
                class="btn btn--secondary"
                type="button"
                @click="clearFilter"
              >
                {{ $t('findRetailer.filter.clearFilter') }}
              </button>
              <button
                class="btn btn--primary grow"
                type="button"
                @click="applyFilter"
              >
                {{ $t('findRetailer.filter.applyFilter', {num: filteredStores.length}) }}
              </button>
            </div>
          </template>
        </GenericSideMenu>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted } from 'vue';
import useVisualAppearancePlugin from '~/composables/useVisualAppearancePlugin';
import { GoogleMap, InfoWindow, Marker, MarkerCluster } from 'vue3-google-map';
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import type { MarkerClustererOptions } from '@googlemaps/markerclusterer';
import FindRetailerStore from '~/components/find-retailer/FindRetailerStore.vue';
import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';
import TextInput from '~/components/form-elements/TextInput.vue';
import LoadProgress from '~/components/generic/LoadProgress.vue';
import useApiFetch from '~/composables/useApiFetch';
import type { RetailersApiResponse, StoreModel, GeocodeResponse } from '#constants/types/norce';
import { useGlobalContentStore } from '~/store/globalContent';

const { isNonEcomSite, showAdminWarning } = useNonEcomGuard();

interface Props {
  _uid: string;
  name: string,
  initialListView: boolean,
  latitude?: number,
  longitude?: number,
  center_type?: string,
  searchRadius?: number, // search radius for city/broad searches, in kilometers (default: 20)
  searchRadiusZip?: number, // search radius for zip/postal code and exact addresses, in kilometers (default: 10)
  defaultSort?: 'distance' | 'exhibitionSize'
  retailerOutsideRadius?: boolean
}
const props = defineProps<{ blok: Props }>();
const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);
const { apiGet, handleApiError } = useApiFetch();
const { $t } = useNuxtApp();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const globalContent = useGlobalContentStore();

// Turn of Points of Interest
const mapStyles = [
  {
    'featureType': 'poi',
    'stylers': [
      {
        'visibility': 'off',
      },
    ],
  },
];

// ——————————————————————————
// UI State
// ——————————————————————————
const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);
const currentTab = ref<'map' | 'list'>(props.blok.initialListView && isMobile.value ? 'list' : 'map');

const showFilterModal = ref(false);
function openFilterModal() { showFilterModal.value = true; }
function closeFilterModal() { showFilterModal.value = false; }
function applyFilter() {
  hasInteracted.value = true;
  closeFilterModal();
}

const hasInteracted = ref(false);
let mapInitialIdleSkipped = false;

// ——————————————————————————
// Map
// ——————————————————————————
const center = computed(() => {
  // If center_type is 'center' and we have stores, calculate center from all stores
  if (props.blok.center_type === 'center' && stores.value.length > 0) {
    const lats = stores.value.map(s => s.latitude);
    const lngs = stores.value.map(s => s.longitude);
    const centerLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
    const centerLng = lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length;
    return { lat: centerLat, lng: centerLng };
  }

  // Otherwise use manual coordinates or default
  return {
    lat: Number(props.blok.latitude) || 53.46627192741455,
    lng: Number(props.blok.longitude) || -1.8334145991382833,
  };
});
const mapRef = ref<InstanceType<typeof GoogleMap> | null>(null);
const currentBounds = ref<google.maps.LatLngBounds | null>(null);
const mapReady = ref(false);
const showMap = ref(currentTab.value === 'map');

// Remove markers/cluster one render before the map unmounts, otherwise the clusterer runs without a map projection
watch(currentTab, async (tab) => {
  if (tab === 'map') {
    showMap.value = true;
    return;
  }
  mapReady.value = false;
  await nextTick();
  showMap.value = false;
});

const clusterOptions: MarkerClustererOptions = {
  renderer: {
    render({ count, position }) {
      const diameter = 40;

      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg"
             width="${diameter}" height="${diameter}">
          <circle
            cx="${diameter/2}" cy="${diameter/2}" r="${(diameter/2) - 2}"
            fill="#A8003B"/>
          <text
            x="50%" y="52%"
            text-anchor="middle" dominant-baseline="middle"
            fill="#FFFFFF"
            font-family="Arial, sans-serif"
            font-size="14px"
          >${count}</text>
        </svg>`;

      const icon: google.maps.Icon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(diameter, diameter),
        anchor:     new google.maps.Point(diameter/2, diameter/2),
      };

      return new google.maps.Marker({
        position,
        icon,
        zIndex: google.maps.Marker.MAX_ZINDEX + count,
      });
    },
  },
};

const customIcon = computed<google.maps.Icon | undefined>(() => {
  // bail out during SSR or before the maps API has loaded
  if (typeof window === 'undefined' || !window.google?.maps?.Size) { return; }
  return {
    url: '/logos/kahrs-logo-round.svg',
    scaledSize: new google.maps.Size(32, 32),
    anchor:     new google.maps.Point(16, 32),
  };
});

const selectedIcon = computed<google.maps.Icon | undefined>(() => {
  // bail out during SSR or before the maps API has loaded
  if (typeof window === 'undefined' || !window.google?.maps?.Size) { return; }
  return {
    url: '/logos/kahrs-logo-round-black.svg',
    scaledSize: new google.maps.Size(32, 32),
    anchor:     new google.maps.Point(16, 32),
  };
});

// Track hovered marker
const hoveredStoreId = ref<string | null>(null);

watch(
  () => ({
    ready: mapRef.value?.ready,
  }),
  ({ ready }) => {
    if (ready) { mapReady.value = true; }
  }
);

function onMapIdle() {
  const map = mapRef.value?.map;
  if (!map) { return; }
  // update bounds (or null if not ready)
  currentBounds.value = map.getBounds() ?? null;

  // skip the very first fire
  if (!mapInitialIdleSkipped) {
    mapInitialIdleSkipped = true;
  } else {
    hasInteracted.value = true;
  }
}

// ——————————————————————————
// Search / Filters / Sort State
// ——————————————————————————
const searchQuery = ref('');
const selectedExhibitions = ref<string[]>([]);
const selectedFloors = ref<string[]>([]);
const sortOption = ref<'distance' | 'exhibitionSize'>(props.blok.defaultSort || 'distance');

// mark interaction if they type or toggle filters (not sort, as that's managed separately)
watch(
  [searchQuery, selectedExhibitions, selectedFloors],
  () => { hasInteracted.value = true; }
);

watch (searchQuery, (newVal) => {
  doSearch();
  if (newVal === '') {
    history.replaceState(
      {},
      '',
      route.path
    );
  } else {
    history.replaceState(
      {},
      '',
      route.path + '?search=' + encodeURI(newVal)
    );
  }
});

// Watch filters and trigger search when they change
watch([selectedExhibitions, selectedFloors, sortOption], () => {
  doSearch();
});

// ——————————————————————————
// Pagination state
// ——————————————————————————
const visibleCount = ref(2);  // Starts with 2 stores shown
const storesPerPage = 2;  // Number of stores to show per page (you can adjust this if needed)

// Update the visible stores based on pagination
const paginatedStores = computed(() => filteredStores.value.slice(0, visibleCount.value));

function loadMore() {
  // Increase visible count by the number of stores per page (2)
  visibleCount.value += storesPerPage;
}

// ——————————————————————————
// Data Models
// ——————————————————————————

const stores = ref<StoreModel[]>([]);

interface Exhibition {
  name: string;
  rating: number; // 1–4
  description: string;
}

const exhibitions = ref<Exhibition[]>([
  {
    name: $t('findRetailer.rating4.name'),
    rating: 4,
    description: $t('findRetailer.rating4.description'),
  },
  {
    name: $t('findRetailer.rating3.name'),
    rating: 3,
    description: $t('findRetailer.rating3.description'),
  },
  {
    name: $t('findRetailer.rating2.name'),
    rating: 2,
    description: $t('findRetailer.rating2.description'),
  },
]);

function getExhibitionDetails(storeExhibition: string): Exhibition | undefined {
  return exhibitions.value.find((e) => e.name === storeExhibition);
}

const MAX_RATING = 4;

// ——————————————————————————
// Helpers: Distance & Geolocation
// ——————————————————————————
const userLocation = ref<{ lat: number; lng: number } | null>(null);

function toRad(deg: number) { return (deg * Math.PI) / 180; }
function getDistanceKm(
  from: { lat: number; lng: number },
  to:   { lat: number; lng: number }
) {
  const R = 6371;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lng - from.lng);
  const a =
      Math.sin(dLat/2)**2 +
      Math.cos(toRad(from.lat)) *
      Math.cos(toRad(to.lat)) *
      Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const pauseBounds = ref(false);

// Check if we have a valid reference point for distance calculations
const hasDistanceReference = computed(() => {
  return userLocation.value !== null || searchQuery.value.trim() !== '';
});

// Track if user has manually changed sort option (set by click handlers in template)
const userHasChangedSort = ref(false);

// Handler for when user manually clicks a sort option
function onSortOptionClick() {
  userHasChangedSort.value = true;
  hasInteracted.value = true;
}

// Auto-manage sort option based on distance reference availability
if (import.meta.client) {
  watch(hasDistanceReference, (hasReference, previousHasReference) => {
    // When distance becomes unavailable and it's currently selected, switch to exhibition size
    if (!hasReference && sortOption.value === 'distance') {
      if (!globalContent.disableRetailerExhibitionDetails) {
        sortOption.value = 'exhibitionSize';
        userHasChangedSort.value = false; // Reset manual change flag
      }
    }
    // When distance becomes available and user hasn't manually changed sort, respect default
    else if (hasReference && previousHasReference === false && !userHasChangedSort.value) {
      sortOption.value = props.blok.defaultSort || 'distance';
    }
  });
}

// Initial setup: if default is distance but no reference, use exhibition size
onMounted(() => {
  if (!hasDistanceReference.value && sortOption.value === 'distance') {
    if (!globalContent.disableRetailerExhibitionDetails) {
      sortOption.value = 'exhibitionSize';
    }
  }
});

function useCurrentLocation() {
  if (!navigator.geolocation) {
    alert('Geolocation not supported');
    return;
  }
  pauseBounds.value = true;
  searchQuery.value = '';
  hasInteracted.value = true;

  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      const loc = { lat: coords.latitude, lng: coords.longitude };
      userLocation.value = loc;

      // Use precise search radius for actual user location
      const searchRadius = props.blok.searchRadiusZip || 10;

      // Filter stores by distance from user location
      const results = stores.value.filter((store) => {
        const dist = getDistanceKm(
          loc,
          { lat: store.latitude, lng: store.longitude }
        );

        // Add distance to store object
        store.distance = dist;

        // Filter by distance
        if (dist >= searchRadius) {
          return false;
        }

        // Apply exhibition filter
        if (
          selectedExhibitions.value.length &&
            !selectedExhibitions.value.includes(store.exhibition)
        ) {
          return false;
        }

        // Apply floor type filter
        if (selectedFloors.value.length) {
          const arr = store.floors.split(',').map(f => f.trim());
          if (!selectedFloors.value.some(f => arr.includes(f))) {
            return false;
          }
        }

        return true;
      });

      // Sort by distance
      results.sort((a,b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));

      // If no results within radius and retailerOutsideRadius is enabled, show nearest store
      if (results.length === 0 && props.blok.retailerOutsideRadius) {
        const nearest = [...stores.value]
          .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
          .at(0);
        filteredStores.value = nearest ? [nearest] : [];
      } else {
        filteredStores.value = results;
      }
      showNear.value = '';

      const map = mapRef.value?.map;
      if (map) {
        map.panTo(loc);

        setTimeout(() => {
          if (mapRef.value?.ready) {
            map.setZoom(12);
          }
        }, 200);
        setTimeout(()=> pauseBounds.value = false, 500);
      }
    },
    () => alert('Unable to retrieve your location')
  );
}

// ——————————————————————————
// Computed: floor types, filtered + sorted, visible in viewport
// ——————————————————————————
const floorTypes = computed(() => {
  const output = [] as string[];
  stores.value.forEach(s =>
    s.floors.split(',').forEach(f => {
      const trimmed = f.trim();
      if (!output.includes(trimmed) && trimmed !== '') {
        output.push(trimmed);
      }
    })
  );
  return output;
});

const filteredStores = ref<StoreModel[]>([]);
const extendingSearch = ref(false);
const showNear = ref('');

const timer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);

const doExtendedSearch = async(nameMatches: StoreModel[] = []) => {
  const term = searchQuery.value.trim().toLowerCase();

  // To test this function you need to be connected to iveo vpn
  const positionLookup = await apiGet<GeocodeResponse>(`/geocode?address=${term}&country=${globalContent.getCountryCode}`);

  if (positionLookup && positionLookup.lat && positionLookup.lng) {
    const types = positionLookup.types || [];
    const locationType = positionLookup.location_type;

    // Determine if this is a precise location (zip/postal code or exact address)
    const isPreciseLocation = (
      types.includes('postal_code') ||
      types.includes('street_address') ||
      locationType === 'ROOFTOP' ||
      locationType === 'RANGE_INTERPOLATED'
    );

    // Use searchRadiusZip for zip codes and exact addresses, searchRadius for cities and broader areas
    const searchRadius = isPreciseLocation
      ? (props.blok.searchRadiusZip || 10)  // Default 10km for precise locations
      : (props.blok.searchRadius || 20);     // Default 20km for cities/broader areas

    // Use city from response
    const city = positionLookup.city || positionLookup.formatted_address;

    const nearbyStores = stores.value.filter((store) => {
      const dist = getDistanceKm(
        { lat: positionLookup.lat, lng: positionLookup.lng },
        { lat: store.latitude, lng: store.longitude }
      );

      // Add distance to store
      store.distance = dist;

      // Filter by distance using appropriate search radius
      if (dist >= searchRadius) {
        return false;
      }

      // Apply exhibition filter
      if (
        selectedExhibitions.value.length &&
          !selectedExhibitions.value.includes(store.exhibition)
      ) {
        return false;
      }

      // Apply floor type filter
      if (selectedFloors.value.length) {
        const arr = store.floors.split(',').map(f => f.trim());
        if (!selectedFloors.value.some(f => arr.includes(f))) {
          return false;
        }
      }

      return true;
    });

    // Create a Set of IDs from nameMatches to avoid duplicates
    const nameMatchIds = new Set(nameMatches.map(s => s.id));

    // Filter out any nearby stores that are already in nameMatches
    const uniqueNearbyStores = nearbyStores.filter(s => !nameMatchIds.has(s.id));

    // Combine: name matches first, then nearby stores
    const combined = [...nameMatches, ...uniqueNearbyStores];

    // Sort combined results based on current sort option
    if (sortOption.value === 'distance') {
      combined.sort((a,b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    }
    else if (sortOption.value === 'exhibitionSize') {
      combined.sort((a,b) => {
        const ra = getExhibitionDetails(a.exhibition)?.rating ?? 0;
        const rb = getExhibitionDetails(b.exhibition)?.rating ?? 0;
        return rb - ra;
      });
    }

    // If no results within radius and retailerOutsideRadius is enabled, show nearest store
    if (combined.length === 0 && props.blok.retailerOutsideRadius) {
      const nearest = [...stores.value]
        .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
        .at(0);
      filteredStores.value = nearest ? [nearest] : [];
      showNear.value = nearest ? $t('findRetailer.near', { city }) : '';
    } else {
      filteredStores.value = combined;
      showNear.value = $t('findRetailer.near', { city });
    }
  } else {
    // If geocoding failed, just show name matches
    filteredStores.value = nameMatches;
  }

  extendingSearch.value = false;
};

const doSearch = () => {
  clearTimeout(timer.value);
  showNear.value = '';

  const term = searchQuery.value.trim().toLowerCase();
  const loc  = userLocation.value;

  // If there's no search term, show all stores with filters applied
  if (!term) {
    const results = stores.value.filter(store => {
      // compute distance if user location is available
      if (loc) {
        store.distance = getDistanceKm(loc, { lat: store.latitude, lng: store.longitude });
      }

      // Apply exhibition filter
      if (
        selectedExhibitions.value.length &&
          !selectedExhibitions.value.includes(store.exhibition)
      ) {
        return false;
      }

      // Apply floor type filter
      if (selectedFloors.value.length) {
        const arr = store.floors.split(',').map(f => f.trim());
        if (!selectedFloors.value.some(f => arr.includes(f))) {
          return false;
        }
      }

      return true;
    });

    // Sort
    if (sortOption.value === 'distance') {
      results.sort((a,b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    }
    else if (sortOption.value === 'exhibitionSize') {
      results.sort((a,b) => {
        const ra = getExhibitionDetails(a.exhibition)?.rating ?? 0;
        const rb = getExhibitionDetails(b.exhibition)?.rating ?? 0;
        return rb - ra;
      });
    }

    filteredStores.value = results;
    return;
  }

  // If there's a search term, find name matches
  const nameMatches = stores.value.filter(store => {
    // Check if name matches
    const nameMatch = store.name.toLowerCase().includes(term);
    if (!nameMatch) {
      return false;
    }

    // Apply exhibition filter
    if (
      selectedExhibitions.value.length &&
        !selectedExhibitions.value.includes(store.exhibition)
    ) {
      return false;
    }

    // Apply floor type filter
    if (selectedFloors.value.length) {
      const arr = store.floors.split(',').map(f => f.trim());
      if (!selectedFloors.value.some(f => arr.includes(f))) {
        return false;
      }
    }

    return true;
  });

  // Always do extended search to get nearby stores too
  extendingSearch.value = true;
  timer.value = setTimeout(()=> doExtendedSearch(nameMatches), 500);
};

// only those in viewport (after first idle)
const visibleStores = computed(() => {
  if (!currentBounds.value) { return filteredStores.value; }
  return filteredStores.value.filter(s =>
      currentBounds.value!.contains({ lat: s.latitude, lng: s.longitude })
  );
});

// Limit markers rendered on map for performance
// When zoomed out, markers are clustered anyway, so we don't need all of them in DOM
const visibleMapStores = computed(() => {
  // If filtered results are small, show all of them (e.g., after search/filter)
  if (filteredStores.value.length <= 200) {
    return filteredStores.value;
  }

  const map = mapRef.value?.map;
  if (!map) { return filteredStores.value.slice(0, 100); } // Initial load: show first 100

  const zoom = map.getZoom() ?? 6;

  // At low zoom (zoomed out), show fewer markers since they're clustered anyway
  // At high zoom (zoomed in), show more markers
  let maxMarkers = 100;
  if (zoom >= 14) { maxMarkers = 500; } // Very zoomed in - show many
  else if (zoom >= 12) { maxMarkers = 300; } // Zoomed in - show more
  else if (zoom >= 10) { maxMarkers = 200; } // Medium zoom
  else if (zoom >= 8) { maxMarkers = 150; } // Zoomed out
  // else stay at 100 for very zoomed out

  // Prioritize stores in current viewport
  if (currentBounds.value) {
    const inBounds = filteredStores.value.filter(s =>
        currentBounds.value!.contains({ lat: s.latitude, lng: s.longitude })
    );

    // If we have fewer than max in bounds, add some outside bounds
    if (inBounds.length < maxMarkers) {
      const outOfBounds = filteredStores.value.filter(s =>
        !currentBounds.value!.contains({ lat: s.latitude, lng: s.longitude })
      );
      return [...inBounds, ...outOfBounds.slice(0, maxMarkers - inBounds.length)];
    }

    return inBounds.slice(0, maxMarkers);
  }

  return filteredStores.value.slice(0, maxMarkers);
});

// ——————————————————————————
// Side‐effects: auto‐fit bounds on filter change
// ——————————————————————————
watch(
  filteredStores,
  (stores) => {
    if (!pauseBounds.value) {
      const map = mapRef.value?.map;
      const api = mapRef.value?.api;
      if (!map || !api || !stores.length) {
        return;
      }

      // Only auto-fit bounds if center_type is 'center', otherwise use manual center
      if (props.blok.center_type === 'center') {
        const bounds = new api.LatLngBounds();
        stores.forEach(s => bounds.extend({ lat: s.latitude, lng: s.longitude }));
        map.fitBounds(bounds);
        if (stores.length === 1) {
          map.setZoom(12);
        }
      }
    }
  },
  { immediate: true }
);

// ——————————————————————————
// Other helpers
// ——————————————————————————
function clearFilter() {
  searchQuery.value = '';
  selectedExhibitions.value = [];
  selectedFloors.value = [];
}

function zoomToStore(store: StoreModel) {
  const map = mapRef.value?.map;
  if (!map) { return; }
  map.panTo({ lat: store.latitude, lng: store.longitude });
  map.setZoom(12);
}

function zoomIn() {
  const map = mapRef.value?.map;
  if (!map) { return; }
  const currentZoom = map.getZoom() ?? 6;
  map.setZoom(currentZoom + 1);
}

function zoomOut() {
  const map = mapRef.value?.map;
  if (!map) { return; }
  const currentZoom = map.getZoom() ?? 6;
  map.setZoom(currentZoom - 1);
}

// Count active filters
const activeFilterCount = computed(
  () => selectedExhibitions.value.length + selectedFloors.value.length
);

// track which store is expanded
const expandedStoreId = ref<string|null>(null);

// Store refs for scrolling
const storeRefs = ref<Record<string, any>>({});

// Watch expandedStoreId and scroll to the store in the list
watch(expandedStoreId, (newStoreId) => {
  if (newStoreId && storeRefs.value[newStoreId]) {
    // Use nextTick to ensure DOM is updated
    nextTick(() => {
      const element = storeRefs.value[newStoreId]?.$el;
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });
  }
});

// Computed property to get the expanded store object
const expandedStore = computed(() => {
  if (!expandedStoreId.value) { return null; }
  return filteredStores.value.find(s => s.id === expandedStoreId.value) ?? null;
});

// InfoWindow open state
const infoWindowOpen = computed({
  get: () => expandedStoreId.value !== null,
  set: (value: boolean) => {
    if (!value) {
      expandedStoreId.value = null;
    }
  },
});

// InfoWindow options with offset
const infoWindowOptions = computed(() => {
  if (!expandedStore.value) { return {}; }

  const options: google.maps.InfoWindowOptions = {
    position: { lat: expandedStore.value.latitude, lng: expandedStore.value.longitude },
  };

  // Add pixel offset if Google Maps API is loaded
  if (typeof window !== 'undefined' && window.google) {
    options.pixelOffset = new google.maps.Size(0, -32);
  }

  return options;
});

// open store
function openStore(storeId: string) {
  expandedStoreId.value = storeId;
}

// closes the details and resets the map zoom
function closeDetails() {
  // 1) collapse the panel
  expandedStoreId.value = null;

  // 2) zoom out to show all filtered stores
  const map = mapRef.value?.map;
  const api = mapRef.value?.api;
  if (map && api && filteredStores.value.length) {
    const bounds = new api.LatLngBounds();
    filteredStores.value.forEach(s =>
      bounds.extend({ lat: s.latitude, lng: s.longitude })
    );
    map.fitBounds(bounds);
    map.setZoom(12);
  }
  // fallback: go back to default center/zoom
  else if (map) {
    map.panTo(center.value);
    map.setZoom(6);
  }
}

// ——————————————————————————
// Load data
// ——————————————————————————

const loaded = ref(false);

const loadRetailers = async() => {
  const res = await apiGet<RetailersApiResponse>('/retailers');
  if (res) {
    // only inventoried stores are shown in the new design (skipped until at least one store has a date)
    const onlyInventoried = globalContent.getNewRetailerDesign && res.stores.some(s => !!s.inventoryDate);
    stores.value = res.stores.map((store, index) => {
      const floors = [] as string[];
      if (store.wood) {
        floors.push($t('findRetailer.kind.wood'));
      }
      if (store.resilient) {
        floors.push($t('findRetailer.kind.resilient'));
      }

      return {
        ...store,
        id: store.name + index,
        floors: floors.join(','),
        exhibition: $t(`findRetailer.rating${store.exibitionRating}.name`),
        address: store.addressLine1 + ' ' + store.addressLine2,
      };
    }).filter((f) => {
      // wrong data from backend
      return f.longitude && f.latitude && f.showonweb && (!onlyInventoried || f.inventoryDate);
    });

    loaded.value = true;

    doSearch();

    if (route.query.search && typeof route.query.search === 'string') {
      setTimeout(()=> {
        searchQuery.value = route.query.search as string;
        doSearch();
      }, 250);
    }

  } else {
    handleApiError();
  }
};

onMounted(()=> {
  loadRetailers();
});

</script>

<style>
.gm-style-iw-c {
  padding: 0 !important;
  border-radius: 0 !important;
}

.gm-style-iw-d {
  padding: 16px;
}

.gm-style-iw-chr {
  position: absolute;
  top: 12px;
  right: 12px;
  height: 24px;
  z-index: 1;
}

.gm-style-iw-chr .gm-ui-hover-effect {
  width: 24px !important;
  height: 24px !important;
}

.gm-style-iw-chr .gm-ui-hover-effect > span {
  width: 16px !important;
  height: 16px !important;
  margin: 4px !important;
}
</style>
